import type { ReactNode } from "react";
import { useState } from "react";

export type Column<T> = {
  key: string;
  label: string;
  render: (row: T) => ReactNode;
  sortKey?: string;
  align?: "l" | "r";
  title?: string;
  /** shown by default in the column chooser */
  default?: boolean;
};

type Props<T> = {
  columns: Column<T>[];
  visible: string[];
  rows: T[];
  rowKey: (row: T) => number;
  sort?: { key: string; dir: "asc" | "desc" };
  onSort?: (key: string) => void;
  selected?: Set<number>;
  onToggle?: (id: number) => void;
  onToggleAll?: (ids: number[]) => void;
  rowClass?: (row: T) => string | undefined;
  emptyText?: string;
  testId?: string;
};

export function DataTable<T>({
  columns,
  visible,
  rows,
  rowKey,
  sort,
  onSort,
  selected,
  onToggle,
  onToggleAll,
  rowClass,
  emptyText = "No rows",
  testId,
}: Props<T>) {
  const cols = columns.filter((c) => visible.includes(c.key));
  const ids = rows.map(rowKey);
  const allSelected =
    selected !== undefined && ids.length > 0 && ids.every((id) => selected.has(id));
  return (
    <table className="tbl" data-testid={testId}>
      <thead>
        <tr>
          {selected ? (
            <th style={{ width: 28 }}>
              <input
                type="checkbox"
                checked={allSelected}
                aria-label="Select all rows on this page"
                onChange={() => onToggleAll?.(ids)}
              />
            </th>
          ) : null}
          {cols.map((c) => {
            const sortable = Boolean(c.sortKey && onSort);
            const active = sort && c.sortKey === sort.key;
            return (
              <th
                key={c.key}
                className={`${c.align === "r" ? "r " : ""}${sortable ? "sortable" : ""}`}
                title={c.title}
                onClick={sortable ? () => onSort?.(c.sortKey as string) : undefined}
                aria-sort={active ? (sort.dir === "asc" ? "ascending" : "descending") : undefined}
              >
                {c.label}
                {active ? (
                  <span style={{ color: "var(--gold)" }}>{sort.dir === "asc" ? " ▲" : " ▼"}</span>
                ) : null}
              </th>
            );
          })}
        </tr>
      </thead>
      <tbody>
        {rows.length === 0 ? (
          <tr>
            <td colSpan={cols.length + (selected ? 1 : 0)} style={{ color: "var(--muted)" }}>
              {emptyText}
            </td>
          </tr>
        ) : null}
        {rows.map((r) => {
          const id = rowKey(r);
          return (
            <tr key={id} className={rowClass?.(r)} aria-selected={selected?.has(id) || undefined}>
              {selected ? (
                <td>
                  <input
                    type="checkbox"
                    checked={selected.has(id)}
                    aria-label={`Select row ${id}`}
                    onChange={() => onToggle?.(id)}
                  />
                </td>
              ) : null}
              {cols.map((c) => (
                <td key={c.key} className={c.align === "r" ? "r" : undefined}>
                  {c.render(r)}
                </td>
              ))}
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

export function ColumnChooser<T>({
  columns,
  visible,
  onChange,
}: {
  columns: Column<T>[];
  visible: string[];
  onChange: (next: string[]) => void;
}) {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative">
      <button
        type="button"
        className="btn btn-sm"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        data-testid="column-chooser"
      >
        Columns
      </button>
      {open ? (
        <div
          className="scroll-thin absolute right-0 z-30 mt-1 max-h-80 w-56 overflow-auto rounded border p-2"
          style={{
            background: "var(--panel)",
            borderColor: "var(--line-2)",
            boxShadow: "var(--shadow)",
          }}
        >
          {columns.map((c) => (
            <label key={c.key} className="flex items-center gap-2 py-0.5 text-[12px]">
              <input
                type="checkbox"
                checked={visible.includes(c.key)}
                onChange={(e) =>
                  onChange(
                    e.target.checked ? [...visible, c.key] : visible.filter((k) => k !== c.key),
                  )
                }
              />
              {c.label}
            </label>
          ))}
          <div className="mt-2 flex justify-between">
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              onClick={() => onChange(columns.filter((c) => c.default).map((c) => c.key))}
            >
              Defaults
            </button>
            <button type="button" className="btn btn-ghost btn-sm" onClick={() => setOpen(false)}>
              Close
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
